import { useEffect, useRef, useState } from "react";
import { Bookmark, Search, Trash2, X } from "lucide-react";

export function pageText(p) {
  if (!p) return "";
  if (p.kind === "note") return `${p.issue.title} ${p.issue.editor_note || ""}`;
  if (p.kind === "contents") return (p.arts || []).map((a) => `${a.title} ${a.author}`).join(" ");
  if (p.kind === "opener") return `${p.a.title} ${p.a.dek} ${p.a.category}`;
  if (p.kind === "body") return `${p.a.author} ${(p.a.body || []).join(" ")}`;
  if (p.kind === "cover") return `${p.issue.title} ${p.issue.summary || ""}`;
  return "";
}

export function pageLabel(p, i) {
  if (p?.kind === "opener" || p?.kind === "body") return p.a.title;
  if (p?.kind === "note") return "From the editor";
  if (p?.kind === "contents") return "Contents";
  if (p?.kind === "cover") return "Cover";
  return `Page ${i + 1}`;
}

export function useSearch(pages, doc) {
  const cache = useRef({});
  useEffect(() => { cache.current = {}; }, [pages, doc]);
  const textOf = async (i) => {
    if (cache.current[i] !== undefined) return cache.current[i];
    const p = pages[i];
    let t = pageText(p);
    if (p?.kind === "pdf" && doc) {
      const page = await doc.getPage(p.n);
      const tc = await page.getTextContent();
      t = tc.items.map((it) => it.str).join(" ");
    }
    cache.current[i] = t;
    return t;
  };
  return async (q) => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    const out = [];
    for (let i = 0; i < pages.length; i += 1) {
      const t = await textOf(i);
      const at = t.toLowerCase().indexOf(needle);
      if (at >= 0) out.push({ page: i, snippet: `${at > 40 ? "…" : ""}${t.slice(Math.max(0, at - 40), at + needle.length + 60)}…` });
    }
    return out;
  };
}

function Shell({ title, onClose, children, testid }) {
  return (
    <aside className="absolute top-0 right-0 bottom-0 w-full sm:w-[360px] bg-[#0D1B3E] shadow-2xl border-l border-white/10 z-20 flex flex-col" data-testid={testid}>
      <div className="h-14 flex items-center justify-between px-5 border-b border-white/10">
        <p className="f-serif text-xl">{title}</p>
        <button onClick={onClose} aria-label="Close panel" className="p-1 text-[#C3CAD9] hover:text-white" data-testid={`${testid}-close`}><X size={18} /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-5">{children}</div>
    </aside>
  );
}

export function BookmarksPanel({ items, signedIn, onGo, onDelete, onClose }) {
  return (
    <Shell title="Bookmarks" onClose={onClose} testid="reader-bookmarks-panel">
      {!signedIn && <p className="text-sm text-[#C3CAD9]">Sign in to bookmark pages and pick up where you left off.</p>}
      {signedIn && !items.length && <p className="text-sm text-[#C3CAD9]">No bookmarks yet. Use the <Bookmark size={13} className="inline" /> button to mark a page.</p>}
      <ul className="space-y-2">
        {items.map((b) => (
          <li key={b.id} className="flex items-center gap-3 border border-white/10 hover:border-white/40 transition-colors">
            <button onClick={() => onGo(b.page)} className="flex-1 text-left px-4 py-3" data-testid={`bookmark-item-${b.page}`}>
              <span className="f-mono text-xs text-[#9FB0D6]">Page {b.page + 1}</span>
              <span className="block f-serif text-lg leading-snug">{b.label || `Page ${b.page + 1}`}</span>
            </button>
            <button onClick={() => onDelete(b.id)} className="p-3 text-[#9FB0D6] hover:text-white" aria-label="Delete bookmark" data-testid={`bookmark-delete-${b.page}`}><Trash2 size={15} /></button>
          </li>
        ))}
      </ul>
    </Shell>
  );
}

export function SearchPanel({ initial, run, onGo, onClose }) {
  const [q, setQ] = useState(initial || "");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const go = async (text) => {
    setBusy(true);
    try { setRes(await run(text)); } catch { setRes([]); }
    setBusy(false);
  };
  useEffect(() => {
    if (initial) { setQ(initial); go(initial); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);
  return (
    <Shell title="Search this issue" onClose={onClose} testid="reader-search-panel">
      <form onSubmit={(e) => { e.preventDefault(); go(q); }} className="flex items-center gap-2 border-b border-white/30 focus-within:border-white pb-2">
        <Search size={16} className="text-[#9FB0D6]" />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find words or phrases…" className="flex-1 bg-transparent outline-none placeholder:text-[#8796B8]" data-testid="reader-search-input" />
      </form>
      {busy && <p className="text-sm text-[#C3CAD9] mt-4">Searching…</p>}
      {res && !busy && <p className="eyebrow !text-[#9FB0D6] mt-5 mb-3" data-testid="reader-search-count">{res.length} {res.length === 1 ? "page" : "pages"} found</p>}
      <ul className="space-y-2">
        {(res || []).map((r) => (
          <li key={r.page}>
            <button onClick={() => onGo(r.page)} className="w-full text-left border border-white/10 hover:border-white/40 px-4 py-3 transition-colors" data-testid={`reader-search-result-${r.page}`}>
              <span className="f-mono text-xs text-[#9FB0D6]">Page {r.page + 1}</span>
              <span className="block text-sm text-[#E3E8F2] mt-1 leading-relaxed">{r.snippet}</span>
            </button>
          </li>
        ))}
      </ul>
    </Shell>
  );
}
